import type { ReactNode } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { TiptapDoc } from '@odo/shared-ui';
import type { VirtualAppointmentCancellation } from '../../../services/virtualAppointments';
import type {
    VirtualAppointmentOverviewPerson,
    VirtualAppointmentOverviewItem
} from '../../../services/virtualAppointmentsOverview';
import {
    VirtualAppointmentOverviewDetail,
    type DescriptionState
} from './VirtualAppointmentOverviewDetail';
import { OverviewActionsFooter } from './OverviewActionsFooter';

interface PaneMessageProps {
    children: ReactNode;
}

function PaneMessage({ children }: PaneMessageProps) {
    return (
        <div className="flex-1 grid place-items-center px-8 text-center text-[12.5px] text-gray-500">
            <div>{children}</div>
        </div>
    );
}

function PaneSkeleton() {
    return (
        <div className="flex-1 px-5 py-5 space-y-5" aria-label="Loading VA">
            <div className="space-y-2">
                <div className="h-4 w-1/3 bg-gray-200 rounded animate-pulse" />
                <div className="h-3 w-1/2 bg-gray-200 rounded animate-pulse" />
            </div>
            <div className="h-12 bg-gray-200 rounded-lg animate-pulse" />
            <div className="h-10 bg-gray-200 rounded-lg animate-pulse" />
            <div className="h-24 bg-gray-200 rounded-lg animate-pulse" />
        </div>
    );
}

interface VirtualAppointmentOverviewPaneProps {
    /** The list row, or the fetched detail for a deep link; null while that is still loading. */
    item: VirtualAppointmentOverviewItem | null;
    failed: boolean;
    descriptionDoc: TiptapDoc | null;
    createdBy: VirtualAppointmentOverviewPerson | null;
    resolvedBy: VirtualAppointmentOverviewPerson | null;
    cancellation: VirtualAppointmentCancellation | null;
    descriptionState: DescriptionState;
    onRetry: () => void;
    doctorName: string | null;
    todayIso: string;
    onClose: () => void;
    onStackedModalChange: (open: boolean) => void;
}

export function VirtualAppointmentOverviewPane({
    item,
    failed,
    descriptionDoc,
    createdBy,
    resolvedBy,
    cancellation,
    descriptionState,
    onRetry,
    doctorName,
    todayIso,
    onClose,
    onStackedModalChange
}: VirtualAppointmentOverviewPaneProps) {
    let body: ReactNode;
    if (item) {
        body = (
            <VirtualAppointmentOverviewDetail
                item={item}
                createdBy={createdBy}
                resolvedBy={resolvedBy}
                cancellation={cancellation}
                doctorName={doctorName}
                todayIso={todayIso}
                description={{ doc: descriptionDoc, state: descriptionState, onRetry }}
            />
        );
    } else if (failed) {
        body = (
            <PaneMessage>
                Couldn&rsquo;t load this VA.{' '}
                <button type="button" onClick={onRetry} className="text-blue-600 hover:underline">
                    Retry
                </button>
            </PaneMessage>
        );
    } else {
        body = <PaneSkeleton />;
    }

    return (
        <aside
            aria-label="VA details"
            className="relative w-[380px] shrink-0 border-l border-gray-200 bg-white flex flex-col min-h-0"
        >
            {/* No header bar: the VA title opens the pane and the close button floats over its corner. */}
            <button
                type="button"
                onClick={onClose}
                aria-label="Close VA details"
                className="absolute top-3 right-3 z-10 h-7 w-7 grid place-items-center rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-800 transition-colors duration-200"
            >
                <XMarkIcon className="h-4 w-4" />
            </button>
            {body}
            {item && (
                <OverviewActionsFooter
                    key={item.id}
                    item={item}
                    onModalOpenChange={onStackedModalChange}
                />
            )}
        </aside>
    );
}
